import React from 'react';
import { DEFAULT_LIST_PAGE_SIZE_OPTIONS } from '../../constants/pagination';

export interface ListPaginationProps {
  page: number;
  perPage: number;
  total: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  pageSizeOptions?: readonly number[];
}

const ListPagination: React.FC<ListPaginationProps> = ({
  page,
  perPage,
  total,
  onPageChange,
  onPerPageChange,
  pageSizeOptions = DEFAULT_LIST_PAGE_SIZE_OPTIONS,
}) => {
  if (total <= 0) {
    return null;
  }

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <div className="px-3 sm:px-4 py-2 border-t border-gray-200 flex flex-wrap items-center justify-between gap-2 bg-gray-50">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-xs sm:text-sm text-gray-600">
          Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} of {total}
        </p>
        <label className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-600">
          <span>Per page</span>
          <select
            value={perPage}
            onChange={(e) => {
              onPerPageChange(Number(e.target.value));
              onPageChange(1);
            }}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page <= 1}
          className="px-3 py-1.5 text-sm font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <label className="flex items-center gap-1.5 text-sm text-gray-600">
          <span>Page</span>
          <select
            value={page}
            onChange={(e) => onPageChange(Number(e.target.value))}
            className="border border-gray-300 rounded px-2 py-1 text-sm min-w-[4rem]"
            aria-label="Go to page"
          >
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <span>of {totalPages}</span>
        </label>
        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          className="px-3 py-1.5 text-sm font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default ListPagination;
